import { useEffect } from 'react';
import { useRegisterSW } from 'virtual:pwa-register/react';
import { toast } from 'sonner';

/**
 * Surfaces a "new version available" prompt when a fresh build has been
 * deployed. Without this, the PWA keeps serving the cached app shell until
 * the user happens to fully reload, so they can run stale code for a while.
 *
 * - Polls the server every 60s while the app is open, so an already-open tab
 *   notices a deploy on its own.
 * - Shows a persistent sonner toast with a "Restart" button that activates the
 *   new service worker and reloads the page.
 */
export default function PWAReloadPrompt() {
  const {
    needRefresh: [needRefresh],
    updateServiceWorker,
  } = useRegisterSW({
    onRegisteredSW(_swUrl, registration) {
      if (registration) {
        setInterval(() => {
          registration.update().catch(() => {});
        }, 60 * 1000);
      }
    },
  });

  useEffect(() => {
    if (!needRefresh) return;
    toast('A new version of HazEasy is available', {
      id: 'pwa-update',
      description: 'Restart to load the latest update.',
      duration: Infinity,
      action: {
        label: 'Restart',
        onClick: () => updateServiceWorker(true),
      },
    });
  }, [needRefresh, updateServiceWorker]);

  return null;
}
